/**
 * The arithmetic behind a multi-select table, and the report of what a bulk
 * action did.
 *
 * Kept apart from the screen that uses it for two reasons. The obvious one is
 * that none of this is about React: selecting rows is set arithmetic, and a
 * partial failure is a sentence. The other is that `portal/frontend` has no
 * test framework, and `npm test` is plain `node --experimental-strip-types`
 * with no bundler behind it — so this module must stay runnable by node
 * alone. That rules out React, JSX and anything resolved by Vite rather than
 * by node; ordinary imports are fine, and `tests/selection.test.ts` uses
 * several.
 *
 * Selections are arrays rather than a `Set` because they cross a React state
 * boundary, where a stable identity for an unchanged value is worth more than
 * O(1) membership on a list of a few dozen rows.
 */

/** Whether the header checkbox is empty, mixed, or full. */
export type SelectionState = "none" | "some" | "all";

/** Add `id` if it is missing, remove it if it is present. */
export function toggle(selected: readonly string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((entry) => entry !== id) : [...selected, id];
}

/**
 * Drop selected ids that the table no longer shows.
 *
 * The Sources list polls, so a source deleted in another tab — or by the
 * scheduler — vanishes from the next payload. A selection still holding it
 * would put a row in the count that nobody can see and send a DELETE that
 * answers 404.
 *
 * Returns `selected` itself when nothing was dropped. This is called on every
 * render, and handing back a fresh array each time would restart anything
 * keyed on the selection's identity.
 *
 * This filters a *read*. It does not write the shorter list back, so a caller
 * holding the unpruned state still holds the vanished ids until something
 * replaces it — which is why the screen feeds this result, never its raw
 * state, into every subsequent `toggle` and `toggleShown`. One interaction
 * is enough to rebase the state on what the table actually shows.
 */
export function retain(
  selected: readonly string[],
  present: readonly string[],
): readonly string[] {
  const kept = selected.filter((id) => present.includes(id));
  return kept.length === selected.length ? selected : kept;
}

/**
 * How full the selection is.
 *
 * An empty table is "none" rather than "all": every row being selected is
 * vacuously true of no rows, and a ticked header checkbox above an empty table
 * claims something was chosen.
 */
export function stateOf(selected: readonly string[], present: readonly string[]): SelectionState {
  if (selected.length === 0 || present.length === 0) return "none";
  return selected.length >= present.length ? "all" : "some";
}

/**
 * What the header checkbox does next: select every row on screen, or clear them.
 *
 * It used to be `nextForHeader`, which answered for a table that draws
 * everything it has — "select all" was `[...present]` and nothing else existed.
 * The Sources screen stopped being such a table when it gained a search and two
 * filters: a selection there can hold rows the filter is hiding. Replacing the
 * whole selection with the shown rows would untick those, and clearing would
 * untick them too — in both cases the header checkbox would have spoken for rows that
 * are not under it, silently, on the one screen whose bulk action cannot be
 * undone.
 *
 * So this only ever adds or removes `shown`, and whatever else is selected
 * survives either way. Full means full *of what is on screen*, which is what
 * `stateOf` is given the same two arguments to decide.
 *
 * Shown rows are appended in the table's order rather than merged in place: the
 * selection is a set, and nothing downstream reads its order.
 */
export function toggleShown(
  selected: readonly string[],
  shown: readonly string[],
): readonly string[] {
  // An empty table is "none" to `stateOf`, so without this a search matching
  // nothing would take the clearing branch below and the array would be rebuilt
  // identically on every render.
  if (shown.length === 0) return selected;
  if (stateOf(retain(selected, shown), shown) === "all") {
    return selected.filter((id) => !shown.includes(id));
  }
  return [...selected, ...shown.filter((id) => !selected.includes(id))];
}

/** One source's fate in a bulk delete: `failure` is null when it was deleted. */
export type DeletionOutcome = {
  id: string;
  name: string;
  failure: string | null;
};

export type DeletionReport = {
  /** Ids the server no longer has — safe to drop from the cache. */
  deleted: string[];
  /** Ids still there, which stay selected so the action can be retried. */
  failed: string[];
  /** Banner text, or null when there is nothing to say. */
  message: string | null;
};

/** How many failed sources are named before the message starts counting. */
const NAMES_SHOWN = 3;

/**
 * Turn the outcomes of a fan-out delete into something worth reading.
 *
 * A bulk delete is many requests, so "it failed" is never the whole truth —
 * the operator needs to know what is gone and what is still there, or the only
 * safe move after an error is to reload and count by hand. Success says
 * nothing: the rows leaving the table already said it.
 */
export function summariseDeletion(outcomes: readonly DeletionOutcome[]): DeletionReport {
  const deleted = outcomes.filter((outcome) => outcome.failure === null);
  const failed = outcomes.filter((outcome) => outcome.failure !== null);

  const report: DeletionReport = {
    deleted: deleted.map((outcome) => outcome.id),
    failed: failed.map((outcome) => outcome.id),
    message: null,
  };
  if (failed.length === 0) return report;

  const names = listNames(failed.map((outcome) => outcome.name));
  // Every failure in one fan-out is usually the same failure — an expired
  // session, the API down — so the first reason speaks for the rest rather
  // than repeating itself once per row.
  const reason = failed[0]?.failure ?? "";
  const trouble = `Could not delete ${names}: ${reason}`;

  report.message =
    deleted.length > 0
      ? `Deleted ${deleted.length} of ${outcomes.length} ${plural(outcomes.length)}. ${trouble}`
      : trouble;
  return report;
}

/** "A", "A and B", "A, B and C", then "A, B, C and 4 more". */
function listNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length <= NAMES_SHOWN) {
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, NAMES_SHOWN).join(", ")} and ${names.length - NAMES_SHOWN} more`;
}

function plural(count: number): string {
  return count === 1 ? "source" : "sources";
}

